import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Computes the top padding a scroll container needs so its content starts
 * exactly `gap` pixels below a fixed/overlaying header.
 *
 * Measured instead of hardcoded because the header height changes with the
 * breakpoint and with optional rows (bookmark, RC part, status badge).
 */
export const useFixedHeaderOffset = ({ headerRef, scrollRef, gap = 10 }) => {
  const [paddingTop, setPaddingTop] = useState(gap);

  const measure = useCallback(() => {
    const header = headerRef.current;
    const scroller = scrollRef.current;
    if (!header || !scroller) return;

    // Padding lives inside the scroller, so reading its top can't feed back into this value.
    const offset = header.getBoundingClientRect().bottom - scroller.getBoundingClientRect().top;
    setPaddingTop(Math.max(gap, Math.round(offset) + gap));
  }, [gap, headerRef, scrollRef]);

  useLayoutEffect(() => {
    measure();

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (observer && headerRef.current) {
      observer.observe(headerRef.current);
    }

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [measure, headerRef]);

  return paddingTop;
};
